/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import { collectionDrawerStore } from '@/stores/store.collection-drawer';
import {
  baseStyles,
  buttonStyles,
  formStyles,
  typographyStyles,
} from '@/styles';
import { UpdateLiveStatusTranslations } from '@/types';
import { i18nInit, setBaseUrl } from '@/utils';

import '@/components/accessibility/fg-aria-live';
import '@/components/fg-checkbox';
import { FgDialog } from '@/components/fg-dialog';
import '@/components/pagination/fg-pagination';

@customElement('fg-collection-drawer')
export class FgCollectionDrawer extends LitElement {
  private collectionDrawerStore = collectionDrawerStore.getState();
  private unsubscribe?: () => void;
  private newCollectionInputRef: Ref<HTMLInputElement> = createRef();
  private newCollectionFormRef: Ref<HTMLFormElement> = createRef();
  private collectionErrorRef: Ref<HTMLDivElement> = createRef();
  private fgDialogRef: Ref<FgDialog> = createRef();

  @property({ type: String })
  baseUrl = '';

  @property({ type: String })
  locale = 'en_US';

  @property({ type: String })
  country = 'US';

  @property({ type: String })
  debtor = '';

  @property({ type: Number })
  customerId?: number | undefined;

  @property({ type: Number })
  pages: number = 8;

  @property({ type: Array })
  collectionList = this.collectionDrawerStore.collectionList;

  @property({ type: Number })
  currentPage = this.collectionDrawerStore.currentPage;

  @property({ type: Object })
  collectionsChecked = this.collectionDrawerStore.collectionsChecked;

  @property({ type: Boolean })
  loading = this.collectionDrawerStore.loading;

  @property({ type: Object })
  liveUpdateStatus = this.collectionDrawerStore.liveUpdateStatus;

  @property()
  private product = '';

  @state()
  private productName = '';

  @state()
  private addCollectionError = false;

  @state()
  newCollection = false;

  async scheduleUpdate() {
    setBaseUrl(this.baseUrl);

    window.i18next = window.i18next || i18nInit(this.locale);
    await window.i18next;

    super.scheduleUpdate();
  }

  willUpdate(changedProperties: PropertyValues) {
    const { setConfig, getCollections } = this.collectionDrawerStore;
    if (
      changedProperties.has('baseUrl') ||
      changedProperties.has('locale') ||
      changedProperties.has('country') ||
      changedProperties.has('debtor') ||
      changedProperties.has('customerId')
    ) {
      setConfig({
        baseUrl: this.baseUrl,
        locale: this.locale,
        country: this.country,
        debtor: this.debtor,
        customerId: this.customerId,
      });
    }

    if (this.collectionList === null || changedProperties.has('product')) {
      getCollections(this.currentPage, this.product);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.unsubscribe = collectionDrawerStore.subscribe(state => {
      this.collectionList = state.collectionList;
      this.pages = state.pages;
      this.currentPage = state.currentPage;
      this.collectionsChecked = state.collectionsChecked;
      this.loading = state.loading;
      this.liveUpdateStatus = state.liveUpdateStatus;
    });

    window.addEventListener(
      'open-collection-dialog',
      this._openCollectionDialogHandler,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.unsubscribe?.();

    window.removeEventListener(
      'open-collection-dialog',
      this._openCollectionDialogHandler,
    );
  }

  private _openCollectionDialogHandler = (
    event: CustomEvent<{ product: string; productName: string }>,
  ) => {
    const { product, productName } = event.detail;

    if (!product || !productName) {
      console.error(
        "Can't open collection dialog without product and/or product name",
      );
      return;
    }

    this.addCollectionError = false;
    this.product = event.detail.product;
    this.productName = event.detail.productName;
    this.fgDialogRef.value?.open();
  };

  createCollection() {
    const inputRef = this.newCollectionInputRef.value;
    const errorRef = this.collectionErrorRef.value;
    const formEl = this.newCollectionFormRef?.value;

    if (!this.customerId || !this.debtor || !errorRef || !inputRef) {
      return;
    }

    errorRef.textContent = '';

    const collectionName = inputRef ? inputRef.value : '';

    if (!collectionName) {
      this.addCollectionError = true;
      errorRef.textContent = t('errors.collectionNameRequired');
      inputRef.focus();

      return;
    }

    const { createCollection } = this.collectionDrawerStore;

    try {
      createCollection(collectionName, this.product);
    } catch (error) {
      console.error('Error creating collection', error);
      this.addCollectionError = true;
      errorRef.textContent = t('errors.cannotCreateCollection');
      inputRef.focus();
      return;
    } finally {
      if (formEl) {
        formEl.classList.remove('active');
        const checkbox = formEl.querySelector?.('input[type="checkbox"]');
        if (checkbox instanceof HTMLInputElement) checkbox.checked = false;
      }

      inputRef.value = '';
      inputRef.focus();
      this.addCollectionError = false;
      this.newCollection = false;
    }
  }

  render() {
    const newCollectionInputRef = ref(this.newCollectionInputRef);
    const newCollectionFormRef = ref(this.newCollectionFormRef);
    const collectionErrorRef = ref(this.collectionErrorRef);
    const { setConfig } = this.collectionDrawerStore;

    return html`
      <fg-dialog
        part="wholesale"
        @dialog-closed=${() => {
          setConfig({
            product: '',
            productName: '',
            collectionList: null,
          });
        }}
        ${ref(this.fgDialogRef)}
        hasAccessibleFocusOnContent
      >
        <span slot="title">${t('headers.collections')}</span>
        <span slot="pre-body">
          ${unsafeHTML(
            t('content.collection.addToOneOrMoreCollections', {
              productName: this.productName,
            }),
          )}
        </span>

        ${this.liveUpdateStatus
          ? html`
              <fg-aria-live
                .announcement=${t(
                  UpdateLiveStatusTranslations[this.liveUpdateStatus.eventName],
                  {
                    collectionName: this.liveUpdateStatus.data?.collectionName,
                    totalCollections:
                      this.liveUpdateStatus.data?.totalCollections,
                  },
                ) || ''}
              ></fg-aria-live>
            `
          : nothing}
        <div>
          <div class="drawer-content">
            <section
              class="new-collection-section"
              aria-labelledby="new-collection-heading"
            >
              <h3 id="new-collection-heading" class="sr-only">
                ${t('accessibility.createNewCollection')}
              </h3>
              <form
                @submit=${(event: Event) => {
                  event.preventDefault();
                  this.createCollection();
                }}
                role="form"
                aria-label="${t('accessibility.createNewCollection') ||
                'Create new collection'}"
                ${newCollectionFormRef}
              >
                <div class="input-group">
                  <label for="new-collection-input" class="sr-only">
                    ${t('accessibility.nameNewCollection')}
                  </label>

                  <div class="checkbox-wrapper">
                    <input
                      aria-hidden="true"
                      class="sr-only"
                      type="checkbox"
                      aria-describedby="new-collection-help"
                      .checked=${this.newCollection}
                      value=${t('accessibility.addNewCollection') || ''}
                      tabindex="-1"
                    />
                    <fg-checkbox
                      class="checkbox"
                      .checked=${this.newCollection}
                      @click=${() => {
                        this.newCollectionInputRef.value?.focus();
                      }}
                    ></fg-checkbox>
                  </div>
                  <div class="input-group-item">
                    <input
                      ${newCollectionInputRef}
                      type="text"
                      id="new-collection-input"
                      class="drawer-input"
                      placeholder="${t('accessibility.addNewCollection') || ''}"
                      aria-describedby="new-collection-help collection-error"
                      aria-invalid="${this.addCollectionError
                        ? 'true'
                        : 'false'}"
                      @keyup=${(event: KeyboardEvent) => {
                        const input = event.target as HTMLInputElement;
                        const hasValue = input.value.trim() !== '';
                        this.newCollection = hasValue;
                        this.newCollectionFormRef?.value?.classList.toggle(
                          'active',
                          hasValue,
                        );
                      }}
                    />
                    <span id="new-collection-help" class="sr-only">
                      ${t('accessibility.insertNameNewCollection')}
                    </span>
                    <div
                      ${collectionErrorRef}
                      id="collection-error"
                      class="error-message"
                      role="alert"
                      aria-hidden="${this.addCollectionError
                        ? 'false'
                        : 'true'}"
                    ></div>
                  </div>
                </div>

                <button
                  type="button"
                  class="button button-primary ${classMap({
                    disabled: !this.newCollection,
                  })}"
                  aria-describedby="create-button-help"
                  aria-disabled=${!this.newCollection}
                  @click=${this.createCollection}
                >
                  ${t('actions.create')}
                  <span id="create-button-help" class="sr-only">
                    ${t('labels.createNewCollection')}
                  </span>
                </button>
              </form>
            </section>
            ${this.loading
              ? html`
                  ${Array.from({ length: this.pages }).map(
                    () => html`
                      <div class="loading-placeholder">
                        <fg-checkbox class="checkbox"></fg-checkbox>
                        <div class="collection-details">
                          <span class="collection-name"></span>
                          <span class="collection-count"></span>
                        </div>
                      </div>
                    `,
                  )}
                `
              : html`
                  <section aria-labelledby="existing-collections-heading">
                    <h3 id="existing-collections-heading" class="sr-only">
                      ${t('accessibility.existingCollections')}
                    </h3>

                    <ul
                      class="collections-list"
                      role="list"
                      aria-label="${t('accessibility.availableCollections') ||
                      'Available collections'}"
                    >
                      ${this.collectionList?.map((collectionItem, index) => {
                        return html`
                          <li>
                            <label
                              class="collection-item"
                              @change=${() => {
                                const { addOrRemoveProductFromCollection } =
                                  this.collectionDrawerStore;

                                this.collectionsChecked = {
                                  ...this.collectionsChecked,
                                  [index]: !this.collectionsChecked[index],
                                };

                                addOrRemoveProductFromCollection(
                                  collectionItem.id,
                                  !this.collectionsChecked[index],
                                  collectionItem.name,
                                  this.product,
                                );
                              }}
                            >
                              <div class="checkbox-wrapper">
                                <input
                                  class="sr-only"
                                  type="checkbox"
                                  aria-describedby="collection-item-${index}"
                                  name="collections"
                                  value="${collectionItem.name}"
                                  .checked=${!!this.collectionsChecked[index]}
                                />
                                <fg-checkbox
                                  class="checkbox"
                                  .checked=${this.collectionsChecked[index]}
                                ></fg-checkbox>
                              </div>
                              <div class="collection-details">
                                <span class="collection-name">
                                  ${collectionItem.name}
                                </span>
                                <span
                                  id="collection-item-${index}"
                                  class="collection-count"
                                >
                                  ${t('content.collection.productCount', {
                                    count: collectionItem.count,
                                  })}
                                </span>
                              </div>
                            </label>
                          </li>
                        `;
                      })}
                    </ul>
                  </section>
                `}
            ${this.pages > 1
              ? html`
                  <fg-pagination
                    part="wholesale"
                    .pages=${this.pages}
                    .currentPage=${this.currentPage}
                    @go-to-page=${(event: CustomEvent<{ page: number }>) => {
                      const { page } = event.detail;
                      const { getCollections } = this.collectionDrawerStore;
                      if (page) {
                        this.currentPage = page;
                        getCollections(page, this.product);
                        this.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  ></fg-pagination>
                `
              : nothing}
          </div>
        </div>
      </fg-dialog>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    formStyles,
    typographyStyles,
    css`
      .loading-placeholder {
        border: 1px solid var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        display: flex;
        flex-direction: row;
        gap: 0.75rem;
        margin-bottom: 1rem;
        min-height: 67px;
        padding: 0.75rem;
      }

      .loading-placeholder .collection-details {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
      }

      .loading-placeholder .collection-name {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: 0.5125rem;
        height: 1.25rem;
        width: 10rem;
      }

      .loading-placeholder .collection-count {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: 0.5rem;
        height: 1rem;
        width: 4rem;
      }

      .drawer-header {
        padding: 1.5rem 1.5rem 1rem;
        position: relative;
        p {
          padding-top: 0.5rem;
        }
      }

      .close-button {
        align-items: center;
        background: none;
        border: none;
        display: flex;
        font-size: 1.5rem;
        color: var(--fg-gray--700, var(--fg-fallback-gray--700));
        cursor: pointer;
        height: 1.5rem;
        justify-content: center;
        position: absolute;
        right: 1rem;
        top: 1rem;
        width: 1.5rem;
      }

      .drawer-content {
        flex: 1;
        padding-top: 1rem;
      }

      section {
        margin-bottom: 1.5rem;
      }

      .new-collection-section form,
      .collection-item {
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border: 1px solid var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        display: flex;
        flex-direction: row;
        margin-bottom: 1rem;
        min-height: 67px;
      }

      .new-collection-section form:has(input[type='checkbox']:checked),
      .collection-item:has(input[type='checkbox']:checked) {
        background: var(
          --fg-success-background,
          --fg-fallback-success-background
        );
        border-color: var(
          --fg-success-content,
          var(--fg-fallback--success-content)
        );
      }

      .input-group {
        display: flex;
        flex-grow: 1;
        gap: 0.75rem;
        padding: 0.75rem;
        position: relative;
      }

      .input-group-item {
        width: 100%;
      }

      .checkbox {
        color: var(--fg-gray--300, var(--fg-fallback-gray--300));
        display: block;
        margin-top: 0.5rem;
        svg {
          height: 1.5rem;
          width: 1.5rem;
        }
      }

      input[type='checkbox']:checked + .checkbox {
        color: var(--fg-success-content, var(--fg-fallback--success-content));
      }

      .drawer-input {
        height: 2.5rem;
        width: 100%;
      }

      .drawer-input[aria-invalid='true'] {
        border-color: var(--fg-error, var(--fg-fallback-error));
      }

      fg-checkbox {
        height: 1.5rem;
        width: 1.5rem;
      }

      .button-primary {
        margin: 1rem 1rem 1rem 0;
        height: 2rem;
      }

      .button-primary.disabled {
        cursor: not-allowed;
        opacity: 0.3;
      }

      .collections-list {
        list-style: none;
        margin: 0;
        padding: 0;
      }

      .collection-item {
        cursor: pointer;
        gap: 0.75rem;
        margin-bottom: 0.5rem;
        padding: 0.5rem 0.75rem;
      }

      .collection-item:hover {
        border-color: var(--fg-gray--300, var(--fg-fallback-gray--300));
        box-shadow: var(--fg-boxshadow, var(--fg-fallback-boxshadow));
      }

      .collection-item .checkbox {
        margin-top: 0.75rem;
      }

      .collection-name {
        color: var(--fg-gray--900, var(--fg-fallback-gray--900));
        display: block;
        font-size: var(--fg-font-size--xl, var(--fg-fallback-font-size--xl));
        font-weight: 600;
        margin-bottom: 0.125rem;
      }

      .collection-count {
        color: var(--fg-gray--800, var(--fg-fallback-gray--800));
        font-size: var(--fg-font-size--sm, var(--fg-fallback-font-size--sm));
        font-weight: 400;
      }

      .error-message {
        background-color: var(
          --fg-error--light,
          var(--fg-fallback-error--light)
        );
        color: var(--fg-error, var(--fg-fallback-error));
        display: none;
        font-size: 14px;
        margin-top: 0.25rem;
        padding: 0.25rem;
      }

      .error-message[aria-hidden='false'] {
        display: block;
      }
    `,
  ];
}
